import { useTranslation } from 'react-i18next'
import { cx } from './cx'
import { pageNumbers, pageRange } from '../../lib/list-state'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const NAV =
  'px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-md transition-colors ' +
  'hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'

/**
 * Pager for a server-paginated table. Extracted from UsersPage, which is still
 * its only caller — it moved here because it is pure presentation and 90 lines
 * of it made the page hard to read, not because anything else needs it yet.
 *
 * `total` and `pageSize` should be the values the *server* echoed back, not the
 * ones just requested: on the render right after a page-size change those
 * differ, and the range would read wrong for a frame.
 */
export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className,
}: {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  className?: string
}) {
  const { t } = useTranslation()
  const { totalPages, rangeStart, rangeEnd } = pageRange(page, pageSize, total)
  const pages = pageNumbers(page, totalPages)

  return (
    <div
      className={cx(
        'border-t border-gray-100 px-5 py-3 flex flex-wrap items-center justify-between gap-3 bg-gray-50/50',
        className,
      )}
    >
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span>{t('common.pageRange', { start: rangeStart, end: rangeEnd, total })}</span>
        <div className="flex items-center gap-1.5">
          <span>{t('common.rowsPerPage')}</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label={t('common.rowsPerPage')}
            className="text-xs px-2 py-1 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label={t('common.previousPage')}
          className={NAV}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {pages.map((p, idx) =>
          p === 'ellipsis' ? (
            <span key={`gap-${idx}`} className="px-1 text-xs text-gray-400">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cx(
                'min-w-[28px] px-2 py-1.5 text-xs font-medium rounded-md border transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                p === page
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'text-gray-600 border-gray-300 hover:bg-white',
              )}
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label={t('common.nextPage')}
          className={NAV}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  )
}
